/*
 This file is part of GNU Taler
 (C) 2023-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>

 SPDX-License-Identifier: AGPL3.0-or-later
*/

import { CancellationToken } from "./CancellationToken.js";
import { Codec } from "./codec.js";
import { makeErrorDetail, TalerError } from "./errors.js";
import { j2s } from "./helpers.js";
import { Logger } from "./logging.js";
import { openPromise } from "./promises.js";
import { RequestThrottler } from "./RequestThrottler.js";
import { TalerErrorCode } from "./taler-error-codes.js";
import { Duration } from "./time.js";
import { TalerErrorDetail } from "./types-taler-wallet.js";
import { URL, URLSearchParams } from "./url.js";

const textEncoder = new TextEncoder();

const logger = new Logger("http.ts");

/**
 * An HTTP response that is returned by all request methods of this library.
 */
export interface HttpResponse {
  requestUrl: string;
  requestMethod: string;
  status: number;
  headers: Headers;
  json(): Promise<any>;
  text(): Promise<string>;
  bytes(): Promise<Uint8Array>;
}

export const DEFAULT_REQUEST_TIMEOUT_MS = 60000;

/**
 * Thrown by awaitNativeRequest when the request timed out.
 */
export class RequestTimeoutError extends Error {
  constructor() {
    super("Request timed out");
    Object.setPrototypeOf(this, RequestTimeoutError.prototype);
    this.name = "RequestTimeoutError";
  }
}

/**
 * Thrown by awaitNativeRequest when the request was cancelled.
 */
export class RequestCancelledError extends Error {
  constructor() {
    super("Request cancelled");
    Object.setPrototypeOf(this, RequestCancelledError.prototype);
    this.name = "RequestCancelledError";
  }
}

/**
 * Await a native, cancellable request, bounded by an optional timeout and an
 * optional cancellation token.
 *
 * On timeout or cancellation the native request is cancelled via cancelFn so
 * that it does not stay in flight, and RequestTimeoutError / RequestCancelled-
 * Error is thrown.  The timer and the cancellation listener are released on
 * every path, so neither leaks when the request fails.
 */
export async function awaitNativeRequest<T>(
  native: { promise: Promise<T>; cancelFn: () => void },
  opts: { timeout?: Duration; cancellationToken?: CancellationToken },
): Promise<T> {
  const abortCap = openPromise<T>();
  let timeoutHandle: ReturnType<typeof setTimeout> | undefined = undefined;
  let unregisterCancel: (() => void) | undefined = undefined;

  const cleanup = (): void => {
    if (timeoutHandle !== undefined) {
      clearTimeout(timeoutHandle);
      timeoutHandle = undefined;
    }
    if (unregisterCancel !== undefined) {
      unregisterCancel();
      unregisterCancel = undefined;
    }
  };

  // Reject before cancelling, not after: cancelFn() makes the native promise
  // reject synchronously, and Promise.race keeps whichever settled first, so
  // cancelling first surfaces the platform's own cancellation error and the
  // typed error below is lost.
  if (opts.timeout && opts.timeout.d_ms !== "forever") {
    timeoutHandle = setTimeout(() => {
      abortCap.reject(new RequestTimeoutError());
      native.cancelFn();
    }, opts.timeout.d_ms);
  }

  if (opts.cancellationToken) {
    unregisterCancel = opts.cancellationToken.onCancelled(() => {
      abortCap.reject(new RequestCancelledError());
      native.cancelFn();
    });
  }

  try {
    return await Promise.race([native.promise, abortCap.promise]);
  } finally {
    cleanup();
  }
}

export interface HttpRequestOptions {
  /**
   * The caller has reviewed this operation and asserts that it does not change
   * server-side application state. Read-only operations must not charge fees
   * or trigger other state-changing operations. Ordinary logging and caching
   * do not count. Set this at individual protocol request sites, never infer
   * it from the HTTP method.
   * Restricted HTTP implementations may reject requests without this marker.
   * This is local policy metadata, not a header sent to the server.
   */
  readOnly?: boolean;
  method?: "POST" | "PATCH" | "PUT" | "GET" | "DELETE";
  headers?: { [name: string]: string | undefined };

  /**
   * Timeout after which the request should be aborted.
   */
  timeout?: Duration;

  /**
   * Cancellation token that should abort the request when
   * cancelled.
   */
  cancellationToken?: CancellationToken;

  body?: string | Uint8Array | object;

  /**
   * How to handle redirects.
   * Same semantics as WHATWG fetch.
   */
  redirect?: "follow" | "error" | "manual";

  /**
   * How to compress the payload
   */
  compress?: "gzip" | "deflate";
}

/**
 * Headers, roughly modeled after the fetch API's headers object.
 */
export interface Headers {
  get(name: string): string | null;
  set(name: string, value: string): void;
  toJSON(): any;
}

export class HeadersImpl {
  private headerMap = new Map<string, string>();

  get(name: string): string | null {
    const r = this.headerMap.get(name.toLowerCase());
    return r === undefined ? null : r;
  }

  set(name: string, value: string): void {
    const normalizedName = name.toLowerCase();
    const existing = this.headerMap.get(normalizedName);
    if (existing !== undefined) {
      this.headerMap.set(normalizedName, existing + "," + value);
    } else {
      this.headerMap.set(normalizedName, value);
    }
  }

  toJSON(): any {
    const m: Record<string, string> = {};
    this.headerMap.forEach((v, k) => (m[k] = v));
    return m;
  }
}

/**
 * Interface for the HTTP request library used by the wallet.
 *
 * The request library is bundled into an interface to make mocking and
 * request tunneling easy.
 */
export interface HttpRequestLibrary {
  /**
   * Make an HTTP POST request with a JSON body.
   */
  fetch(url: string, opt?: HttpRequestOptions): Promise<HttpResponse>;
}

type TalerErrorResponse = {
  code: number;
} & unknown;

type ResponseOrError<T> =
  | { isError: false; response: T }
  | { isError: true; talerErrorResponse: TalerErrorResponse };

/**
 * Read Taler error details from an HTTP response.
 */
export async function readTalerErrorResponse(
  httpResponse: HttpResponse,
): Promise<TalerErrorDetail> {
  const contentType = httpResponse.headers.get("content-type");
  let mediaType: string | undefined = undefined;
  if (contentType) {
    mediaType = contentType.split(";")[0].trim().toLowerCase();
  }
  if (mediaType !== "application/json") {
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
        contentType: mediaType || "<null>",
      },
      "Error response did not even contain JSON. The request URL might be wrong or the service might be unavailable.",
    );
  }
  let errJson;
  try {
    errJson = await httpResponse.json();
  } catch (e) {
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
        validationError: e instanceof Error ? e.message : String(e),
      },
      "Couldn't parse JSON format from error response",
    );
  }

  const talerErrorCode = errJson?.code;
  if (typeof talerErrorCode !== "number") {
    logger.warn(
      `malformed error response (status ${httpResponse.status}): ${j2s(
        errJson,
      )}`,
    );
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
      },
      "Error response did not contain error code",
    );
  }
  return errJson;
}

export async function readUnexpectedResponseDetails(
  httpResponse: HttpResponse,
): Promise<TalerErrorDetail> {
  let errJson;
  try {
    errJson = await httpResponse.json();
  } catch (e) {
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
        validationError: e instanceof Error ? e.message : String(e),
      },
      "Couldn't parse JSON format from error response",
    );
  }
  const talerErrorCode = errJson?.code;
  if (typeof talerErrorCode !== "number") {
    return makeErrorDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
      },
      "Error response did not contain error code",
    );
  }
  return makeErrorDetail(
    TalerErrorCode.WALLET_UNEXPECTED_REQUEST_ERROR,
    {
      requestUrl: httpResponse.requestUrl,
      requestMethod: httpResponse.requestMethod,
      httpStatusCode: httpResponse.status,
      errorResponse: errJson,
    },
    `Unexpected HTTP status (${httpResponse.status}) in response`,
  );
}

export async function readSuccessResponseJsonOrErrorCode<T>(
  httpResponse: HttpResponse,
  codec: Codec<T>,
): Promise<ResponseOrError<T>> {
  if (!(httpResponse.status >= 200 && httpResponse.status < 300)) {
    return {
      isError: true,
      talerErrorResponse: await readTalerErrorResponse(httpResponse),
    };
  }
  let respJson;
  try {
    respJson = await httpResponse.json();
  } catch (e) {
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
        validationError: e instanceof Error ? e.message : String(e),
      },
      "Couldn't parse JSON format from response",
    );
  }
  let parsedResponse: T;
  try {
    parsedResponse = codec.decode(respJson);
  } catch (e) {
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
        validationError: e instanceof Error ? e.message : String(e),
      },
      "Response invalid",
    );
  }
  return {
    isError: false,
    response: parsedResponse,
  };
}

export async function readResponseJsonOrThrow<T>(
  httpResponse: HttpResponse,
  codec: Codec<T>,
): Promise<T> {
  let respJson;
  try {
    respJson = await httpResponse.json();
  } catch (e) {
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
        validationError: e instanceof Error ? e.message : String(e),
      },
      "Couldn't parse JSON format from response",
    );
  }
  let parsedResponse: T;
  try {
    parsedResponse = codec.decode(respJson);
  } catch (e) {
    throw TalerError.fromDetail(
      TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
      {
        requestUrl: httpResponse.requestUrl,
        requestMethod: httpResponse.requestMethod,
        httpStatusCode: httpResponse.status,
        response: await httpResponse.text(),
        validationError: e instanceof Error ? e.message : String(e),
      },
      "Response invalid",
    );
  }
  return parsedResponse;
}

type HttpErrorDetails = {
  requestUrl: string;
  requestMethod: string;
  httpStatusCode: number;
};

export function getHttpResponseErrorDetails(
  httpResponse: HttpResponse,
): HttpErrorDetails {
  return {
    requestUrl: httpResponse.requestUrl,
    requestMethod: httpResponse.requestMethod,
    httpStatusCode: httpResponse.status,
  };
}

export function throwUnexpectedRequestError(
  httpResponse: HttpResponse,
  talerErrorResponse: TalerErrorResponse,
): never {
  const errorDetails = {
    requestUrl: httpResponse.requestUrl,
    requestMethod: httpResponse.requestMethod,
    httpStatusCode: httpResponse.status,
    errorResponse: talerErrorResponse,
  };
  logger.trace(`unexpected request error: ${j2s(errorDetails)}`);
  throw TalerError.fromDetail(
    TalerErrorCode.WALLET_UNEXPECTED_REQUEST_ERROR,
    errorDetails,
    `Unexpected HTTP status ${httpResponse.status} in response`,
  );
}

export async function readSuccessResponseJsonOrThrow<T>(
  httpResponse: HttpResponse,
  codec: Codec<T>,
): Promise<T> {
  const r = await readSuccessResponseJsonOrErrorCode(httpResponse, codec);
  if (!r.isError) {
    return r.response;
  }
  throwUnexpectedRequestError(httpResponse, r.talerErrorResponse);
}

// The type parameter is retained for compatibility with existing callers.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export async function expectSuccessResponseOrThrow<T>(
  httpResponse: HttpResponse,
): Promise<void> {
  if (httpResponse.status >= 200 && httpResponse.status <= 299) {
    return;
  }
  const errResp = await readTalerErrorResponse(httpResponse);
  throwUnexpectedRequestError(httpResponse, errResp);
}

export interface HttpLibArgs {
  enableThrottling?: boolean;
  /**
   * Only allow HTTPS connections, not plain http.
   */
  requireTls?: boolean;
  printAsCurl?: boolean;
}

export function encodeBody(body: unknown): Uint8Array {
  if (body == null) {
    return new Uint8Array(0);
  }
  if (typeof body === "string") {
    return textEncoder.encode(body);
  } else if (ArrayBuffer.isView(body)) {
    return new Uint8Array(body.buffer, body.byteOffset, body.byteLength);
  } else if (body instanceof ArrayBuffer) {
    return new Uint8Array(body);
  } else if (body instanceof URLSearchParams) {
    return textEncoder.encode(body.toString());
  } else if (typeof body === "object" && body.constructor.name === "FormData") {
    return new Uint8Array(body as ArrayBuffer);
  } else if (typeof body === "object") {
    return textEncoder.encode(JSON.stringify(body));
  }
  throw new TypeError("unsupported request body type");
}

export function getDefaultHeaders(
  method: string,
  hasBody = false,
): Record<string, string> {
  const headers: Record<string, string> = {};

  if (
    method === "POST" ||
    method === "PUT" ||
    method === "PATCH" ||
    (method === "DELETE" && hasBody)
  ) {
    // Default to JSON if we have a body
    headers["Content-Type"] = "application/json";
  }

  headers["Accept"] = "application/json";

  return headers;
}

/**
 * Raw HTTP response, as reported by a platform-specific HttpRawLib.
 *
 * Deliberately minimal: everything that can be derived from the bytes
 * (text, JSON, and the error reporting around them) is handled once in
 * HttpLib instead of being redone per platform.
 */
export interface HttpRawResponse {
  status: number;
  headers: Headers;
  bytes(): Promise<Uint8Array>;
}

/**
 * A fully resolved request, as handed to a platform-specific HttpRawLib.
 *
 * Throttling, the protocol check, header defaults, body encoding and
 * compression have already been applied by HttpLib, so an implementation only
 * has to move the bytes.
 */
export interface HttpRawRequestOptions {
  method: "POST" | "PATCH" | "PUT" | "GET" | "DELETE";
  headers: Record<string, string>;

  /**
   * Already encoded and, if requested, already compressed.  Undefined for
   * methods that carry no body.
   */
  body: Uint8Array | undefined;

  /**
   * Timeout in milliseconds, or undefined to wait indefinitely.
   */
  timeoutMs: number | undefined;

  cancellationToken?: CancellationToken;
  redirect?: "follow" | "error" | "manual";
}

/**
 * Platform-specific part of the HTTP stack.
 *
 * Implementations report a timeout by throwing RequestTimeoutError and a
 * cancellation by throwing RequestCancelledError; any other failure is thrown
 * as whatever the platform produced.  Translating those into TalerError with
 * the request URL, method and status attached is HttpLib's job, so that every
 * platform surfaces the same error details.
 */
export interface HttpRawLib {
  fetch(url: string, opt: HttpRawRequestOptions): Promise<HttpRawResponse>;

  /**
   * Compress a request body.  Optional: a platform that cannot compress
   * (currently qtart) leaves the body untouched and the request is sent
   * without a Content-Encoding header.
   */
  compress?(alg: "gzip" | "deflate", body: Uint8Array): Promise<Uint8Array>;
}

const textDecoder = new TextDecoder();

/**
 * The HTTP request library used everywhere in the wallet.
 *
 * All the policy lives here — throttling, TLS enforcement, default headers,
 * body encoding and compression, timeout defaults, error mapping, response
 * body caching and trace logging — so that the node, qtart and browser
 * backends only have to implement HttpRawLib.
 */
export class HttpLib implements HttpRequestLibrary {
  private throttle = new RequestThrottler();
  private throttlingEnabled: boolean;
  private requireTls: boolean;
  private idCounter = 1;
  private raw: HttpRawLib;

  constructor(raw: HttpRawLib, args?: HttpLibArgs) {
    this.raw = raw;
    this.throttlingEnabled = args?.enableThrottling ?? true;
    this.requireTls = args?.requireTls ?? false;
  }

  /**
   * Set whether requests should be throttled.
   */
  setThrottling(enabled: boolean): void {
    this.throttlingEnabled = enabled;
  }

  async fetch(
    requestUrl: string,
    opt?: HttpRequestOptions,
  ): Promise<HttpResponse> {
    const rid = this.idCounter++;
    const requestMethod = opt?.method ?? "GET";
    const parsedUrl = new URL(requestUrl);

    if (this.throttlingEnabled && this.throttle.applyThrottle(requestUrl)) {
      throw TalerError.fromDetail(
        TalerErrorCode.WALLET_HTTP_REQUEST_THROTTLED,
        {
          requestMethod,
          requestUrl,
          throttleStats: this.throttle.getThrottleStats(requestUrl),
        },
        `request ${rid} to ${parsedUrl.origin} was throttled`,
      );
    }

    if (parsedUrl.protocol !== "https:" && parsedUrl.protocol !== "http:") {
      throw Error(`unsupported protocol (${parsedUrl.protocol})`);
    }
    if (this.requireTls && parsedUrl.protocol !== "https:") {
      throw TalerError.fromDetail(
        TalerErrorCode.WALLET_NETWORK_ERROR,
        {
          requestMethod,
          requestUrl,
        },
        `request ${rid} to ${parsedUrl.origin} is not possible with protocol ${parsedUrl.protocol}`,
      );
    }

    // An absent timeout means the default, an explicit "forever" means no
    // timeout at all.
    let timeoutMs: number | undefined;
    if (opt?.timeout === undefined) {
      timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS;
    } else if (opt.timeout.d_ms === "forever") {
      timeoutMs = undefined;
    } else {
      timeoutMs = opt.timeout.d_ms;
    }

    const headers = getDefaultHeaders(requestMethod, opt?.body != null);
    if (opt?.headers != null) {
      for (const [key, value] of Object.entries(opt.headers)) {
        if (value === undefined) continue;
        headers[key] = value;
      }
    }

    let body: Uint8Array | undefined;
    if (
      requestMethod === "POST" ||
      requestMethod === "PATCH" ||
      requestMethod === "PUT"
    ) {
      body = encodeBody(opt?.body);
    } else if (requestMethod === "DELETE" && opt?.body != null) {
      body = encodeBody(opt.body);
    }

    if (opt?.body instanceof URLSearchParams) {
      headers["Content-Type"] = "application/x-www-form-urlencoded";
    } else if (
      typeof opt?.body === "object" &&
      opt.body.constructor.name === "FormData"
    ) {
      // The default headers assume JSON; for FormData the browser has to
      // generate the content type itself, boundary included.
      delete headers["Content-Type"];
    }

    if (body != null && opt?.compress != null) {
      if (this.raw.compress != null) {
        body = await this.raw.compress(opt.compress, body);
        headers["Content-Encoding"] = opt.compress;
      } else {
        logger.warn(
          `request ${rid} asked for ${opt.compress}, but this platform cannot compress; sending uncompressed`,
        );
      }
    }

    if (logger.shouldLogTrace()) {
      const timeoutInfo =
        timeoutMs === undefined ? "no timeout" : `timeout ${timeoutMs}ms`;
      if (opt?.body != null) {
        logger.trace(
          `request ${rid} ${requestMethod} ${requestUrl} (${timeoutInfo}): ${j2s(
            opt.body,
          )}`,
        );
      } else {
        logger.trace(
          `request ${rid} ${requestMethod} ${requestUrl} (${timeoutInfo})`,
        );
      }
    }

    let resp: HttpRawResponse;
    try {
      resp = await this.raw.fetch(requestUrl, {
        method: requestMethod,
        headers,
        body,
        timeoutMs,
        cancellationToken: opt?.cancellationToken,
        redirect: opt?.redirect,
      });
    } catch (cause) {
      throw this.mapRequestError(
        cause,
        rid,
        requestUrl,
        requestMethod,
        timeoutMs,
      );
    }

    logger.trace(`request ${rid} status code ${resp.status}`);

    // The response body gets read more than once in a few places (an error
    // path that reports the raw text after JSON parsing failed, for one), so
    // the bytes are pulled from the platform exactly once and kept.
    let cachedBytes: Uint8Array | undefined;
    const readBytes = async (): Promise<Uint8Array> => {
      if (cachedBytes === undefined) {
        cachedBytes = await resp.bytes();
      }
      return cachedBytes;
    };

    const decodeText = async (): Promise<string> => {
      const bytes = await readBytes();
      try {
        return textDecoder.decode(bytes);
      } catch (e) {
        throw TalerError.fromDetail(
          TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
          {
            requestUrl,
            requestMethod,
            httpStatusCode: resp.status,
            validationError: e instanceof Error ? e.message : String(e),
          },
          "Invalid text from HTTP response",
        );
      }
    };

    const readText = async (): Promise<string> => {
      const text = await decodeText();
      if (logger.shouldLogTrace()) {
        logger.trace(`request ${rid} TEXT: ${text}`);
      }
      return text;
    };

    const readJson = async (): Promise<any> => {
      const text = await decodeText();
      let json: any;
      try {
        json = JSON.parse(text);
      } catch (e) {
        throw TalerError.fromDetail(
          TalerErrorCode.WALLET_RECEIVED_MALFORMED_RESPONSE,
          {
            requestUrl,
            requestMethod,
            response: text,
            httpStatusCode: resp.status,
            validationError: e instanceof Error ? e.message : String(e),
          },
          e instanceof Error
            ? `Invalid JSON from HTTP response: ${e.message}`
            : "Invalid JSON from HTTP response",
        );
      }
      if (logger.shouldLogTrace()) {
        logger.trace(`request ${rid} JSON: ${j2s(json)}`);
      }
      return json;
    };

    return {
      requestUrl,
      requestMethod,
      status: resp.status,
      headers: resp.headers,
      bytes: readBytes,
      text: readText,
      json: readJson,
    };
  }

  /**
   * Turn whatever a raw implementation threw into a TalerError that carries
   * the request URL and method.
   */
  private mapRequestError(
    cause: unknown,
    rid: number,
    requestUrl: string,
    requestMethod: string,
    timeoutMs: number | undefined,
  ): TalerError {
    if (cause instanceof RequestCancelledError) {
      logger.trace(`request ${rid} cancelled`);
      return TalerError.fromDetail(
        TalerErrorCode.WALLET_CORE_REQUEST_CANCELLED,
        {},
        `Request cancelled`,
      );
    }
    if (cause instanceof RequestTimeoutError) {
      logger.trace(`request ${rid} timed out (timeout ${timeoutMs})`);
      return TalerError.fromDetail(
        TalerErrorCode.WALLET_HTTP_REQUEST_GENERIC_TIMEOUT,
        {
          requestUrl,
          requestMethod,
          timeoutMs: timeoutMs ?? 0,
        },
        `Request timed out after ${timeoutMs} ms`,
      );
    }
    const message = cause instanceof Error ? cause.message : String(cause);
    logger.trace(`request ${rid} failed with '${message}'`);
    return TalerError.fromDetail(
      TalerErrorCode.WALLET_NETWORK_ERROR,
      {
        requestUrl,
        requestMethod,
      },
      `HTTP request failed: ${message}`,
      cause instanceof Error ? cause : undefined,
    );
  }
}

/**
 * Dummy response, do not use.
 */
export const dummyHttpResponse: HttpResponse = {
  async bytes() {
    return new Uint8Array();
  },
  headers: new HeadersImpl(),
  async json() {
    return {};
  },
  requestMethod: "dummy",
  requestUrl: "dummy",
  status: 0,
  async text() {
    return "";
  },
};
